"use client";

import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import {
  checkoutCurrency,
  formatMoneyAmount,
  formatUsdAmount,
  persistCurrency,
  PUBLIC_CURRENCY_SELECTOR_ENABLED,
  readStoredCurrency,
  type CheckoutCurrency,
  type DisplayCurrency,
} from "@/greenpak/display-currency";

type CurrencyContextValue = {
  currency: DisplayCurrency;
  checkout: CheckoutCurrency;
  setCurrency: (currency: DisplayCurrency) => void;
  formatUsd: (amountUsd: number, options?: { prefix?: string; suffix?: string }) => string;
  formatMoney: (
    amount: number,
    sourceCurrency: CheckoutCurrency,
    options?: { prefix?: string; suffix?: string },
  ) => string;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const currencyListeners = new Set<() => void>();
// In-memory override so switching still works if localStorage is unavailable.
let sessionCurrency: DisplayCurrency | null = null;

function subscribeCurrency(listener: () => void) {
  currencyListeners.add(listener);
  return () => currencyListeners.delete(listener);
}

function readCurrencySnapshot(): DisplayCurrency {
  if (!PUBLIC_CURRENCY_SELECTOR_ENABLED) return "PKR";
  return sessionCurrency ?? readStoredCurrency();
}

function readServerCurrency(): DisplayCurrency {
  return "PKR";
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const currency = useSyncExternalStore(subscribeCurrency, readCurrencySnapshot, readServerCurrency);

  const setCurrency = (next: DisplayCurrency) => {
    if (!PUBLIC_CURRENCY_SELECTOR_ENABLED) return;
    sessionCurrency = next;
    persistCurrency(next);
    currencyListeners.forEach((listener) => listener());
  };

  const value = useMemo<CurrencyContextValue>(
    () => ({
      currency,
      checkout: checkoutCurrency(currency),
      setCurrency,
      formatUsd: (amountUsd, options) => formatUsdAmount(amountUsd, currency, options),
      formatMoney: (amount, sourceCurrency, options) =>
        formatMoneyAmount(amount, sourceCurrency, currency, options),
    }),
    [currency],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useDisplayCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error("useDisplayCurrency must be used within CurrencyProvider");
  }
  return ctx;
}
